import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import { Autocomplete, Grid, MenuItem, TextField } from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import List from '@mui/material/List';
import React, { useEffect, useMemo, useState } from 'react';

import ButtonMenu from '~components/ButtonMenu';
import EmptyState from '~components/EmptyState';
import useDebounce from '~hooks/useDebounce';
import usePublicHolidays from '~hooks/usePublicHoliday';
import { PublicHolidays } from '~hooks/usePublicHoliday/domain';
import { getTimezoneList } from '~pages/Dialler/api';
import DownloadPublicHolidayModal from '~pages/SystemManagement/PublicHolidayList/DownloadPublicHolidayModal/DownloadPublicHolidayModal';
import ImportPublicHolidayModal from '~pages/SystemManagement/PublicHolidayList/ImportPublicHolidayModal/index';
import PublicHolidayListCard from '~pages/SystemManagement/PublicHolidayList/PublicHolidayListCard';
import UpdatePublicHolidayModal from '~pages/SystemManagement/PublicHolidayList/UpdatePublicHolidayModal';
import { useAppConfiguration } from '~providers/AppConfigurationProvider';
import { useNotification } from '~providers/NotificationProvider';
import { APIError } from '~services/Errors';

const PublicHolidayList = () => {
  const { pushNotification } = useNotification();
  const {
    holidays,
    search,
    upload,
    download,
    update,
    remove,
    timezoneFilter,
    postcodeFilter,
    setTimezoneFilter,
    setPostcodeFilter,
    setYearFilter,
    yearFilter,
    intersectionObserverRef: lastPublicHolidayElementRef,
    setSearch,
  } = usePublicHolidays();
  const appConfig = useAppConfiguration();
  const [timezoneList, setTimezoneList] = useState<string[]>([]);
  const [editablePublicHoliday, setEditablePublicHoliday] = useState<PublicHolidays | undefined>(undefined);
  const [ImportModalOpen, setImportModalOpen] = useState<boolean>(false);
  const [DownloadModalOpen, setDownloadModalOpen] = useState<boolean>(false);
  const [postcodeSearch, setPostcodeSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const debouncedSearch = useDebounce(searchInput, 500);
  const debouncedPostcode = useDebounce(postcodeSearch, 500);
  const currentYear = new Date().getFullYear();
  const hasUserAppliedFilter = searchInput !== '' || postcodeSearch !== '' || timezoneFilter.length > 0;

  const activeFilterDescription = [
    search && `Search: "${search}"`,
    timezoneFilter.length > 0 && `Timezones: ${timezoneFilter.join(', ')}`,
    postcodeFilter && `Postcode: ${postcodeFilter}`,
    `Year: ${yearFilter}`,
  ]
    .filter(Boolean)
    .join('\n');

  const yearList = useMemo(() => {
    const years: number[] = [];
    // currentYear + 2 to allow importing next year's holidays in advance. Adjust if further years  is needed.
    for (let year = currentYear + 2; year >= 1980; year--) {
      years.push(year);
    }
    return years;
  }, [currentYear]);

  useEffect(() => {
    setSearch(debouncedSearch);
  }, [debouncedSearch]);

  useEffect(() => {
    setPostcodeFilter(debouncedPostcode);
  }, [debouncedPostcode]);

  useEffect(() => {
    const fetchTimezoneList = async () => {
      try {
        const resp = await getTimezoneList(appConfig.web.timezonePrefixes);
        setTimezoneList(resp);
      } catch (e) {
        pushNotification('error', 'Unable to fetch timezone list');
      }
    };

    fetchTimezoneList();
  }, [appConfig.web.timezonePrefixes]);

  const toggleImportModal = () => {
    setImportModalOpen((prev) => !prev);
  };

  const handleEditOpen = (holiday: PublicHolidays) => {
    setEditablePublicHoliday(holiday);
  };

  const handleEditClose = () => {
    setEditablePublicHoliday(undefined);
  };

  const handleDelete = async (holiday: PublicHolidays) => {
    try {
      await remove(holiday);
      pushNotification('success', `Deleted ${holiday.name}`);
    } catch (e) {
      pushNotification('error', e instanceof APIError ? e.message : 'Failed to delete');
    }
  };

  return (
    <>
      <Grid
        container
        spacing={1}
        sx={{
          alignContent: 'center',
        }}>
        <Grid
          size={{
            xs: 12,
            md: 3,
          }}>
          <TextField
            fullWidth
            size='small'
            variant='outlined'
            label='Search by Keywords'
            id='public-holiday-search'
            name='public-holiday-search'
            onChange={(e) => {
              const value = e.target.value;
              setSearchInput(value);
              if (value === '') {
                setSearch('');
              }
            }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 3,
          }}>
          <Autocomplete
            multiple
            size='small'
            options={timezoneList}
            disableCloseOnSelect
            renderOption={(props, option, { selected }) => (
              <li {...props}>
                <Checkbox
                  icon={<CheckBoxOutlineBlankIcon fontSize='small' />}
                  checkedIcon={<CheckBoxIcon fontSize='small' />}
                  checked={selected}
                  style={{ marginRight: 8 }}
                />
                {option}
              </li>
            )}
            renderInput={(params) => <TextField {...params} variant='outlined' label='Filter by Timezone' />}
            onChange={(_, value) => setTimezoneFilter(value)}
          />
        </Grid>
        <Grid
          size={{
            xs: 12,
            md: 2,
          }}>
          <TextField
            size='small'
            fullWidth
            variant='outlined'
            label='Search by Postcode'
            slotProps={{
              htmlInput: {
                maxLength: 4,
              },
            }}
            onChange={(e) => {
              const value = e.target.value.replace(/\D/g, '');
              setPostcodeSearch(value);
              if (value === '') {
                setPostcodeFilter('');
              }
            }}
          />
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 2,
          }}>
          <TextField
            select
            fullWidth
            size='small'
            variant='outlined'
            label='Search by Year'
            slotProps={{
              select: {
                MenuProps: {
                  slotProps: { paper: { style: { maxHeight: 300 } } },
                },
              },
            }}
            value={yearFilter}
            onChange={(e) => setYearFilter(Number(e.target.value))}>
            {yearList.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </TextField>
        </Grid>

        <Grid
          size={{
            xs: 12,
            md: 2,
          }}>
          <ButtonMenu
            title='Actions'
            actions={[
              {
                title: 'Import Public Holiday',
                func: () => {
                  setImportModalOpen(true);
                },
              },
              {
                title: 'Download Public Holiday by Year',
                func: () => {
                  setDownloadModalOpen(true);
                },
              },
            ]}
          />
        </Grid>
      </Grid>
      {holidays.length > 0 && (
        <List>
          {holidays.map((holiday, index) => (
            <PublicHolidayListCard
              key={`${holiday.year}-${holiday.month}-${holiday.day}-${holiday.name}`}
              ref={index === holidays.length - 1 ? lastPublicHolidayElementRef : undefined}
              publicHolidayList={holiday}
              onEdit={() => handleEditOpen(holiday)}
              onDelete={() => handleDelete(holiday)}
            />
          ))}
        </List>
      )}
      {holidays.length === 0 && hasUserAppliedFilter && (
        <EmptyState
          type='no-records-found'
          text='No public holidays found matching your filters'
          subText={activeFilterDescription}
        />
      )}
      {holidays.length === 0 && !hasUserAppliedFilter && (
        <EmptyState
          type='no-items-1'
          text={`No public holidays found for ${yearFilter}`}
          subText='Try a different year or import public holidays to get started.'
        />
      )}
      <UpdatePublicHolidayModal
        open={Boolean(editablePublicHoliday)}
        publicHoliday={editablePublicHoliday}
        timezoneList={timezoneList}
        onClose={handleEditClose}
        onAccept={async (oldDate, oldName, data) => {
          try {
            await update(oldDate, oldName, data);
            pushNotification('success', `Updated ${data.name}`);
            handleEditClose();
          } catch (e) {
            pushNotification('error', e instanceof APIError ? e.message : 'Update failed');
            throw e;
          }
        }}
      />
      <DownloadPublicHolidayModal
        open={DownloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        onDownload={async (year: number) => {
          try {
            await download(year);
            pushNotification('success', `Downloaded public holidays for ${year}`);
          } catch (e) {
            pushNotification('error', `Failed to download public holidays for ${year}`);
          }
        }}
      />
      <ImportPublicHolidayModal open={ImportModalOpen} onClose={toggleImportModal} onUpload={upload} />
    </>
  );
};

export default PublicHolidayList;
